import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navigation, MapPin, Radio } from "lucide-react";
import { apiBaseUrl, authHeaders, toUtcDate, isAdmin } from "./shared";
import LoadingState from "./LoadingState";
import EnRouteMapModal from "../../components/EnRouteMapModal";

// LIVE operational board: trucks with no current load that are deadheading toward
// a known next pickup (recorded deadhead intent). Distinct from Idle Movement
// (which is movement with NO booked destination). DB/telematics-derived; the
// board never triggers external routing (miles remaining is straight-line).

const REFRESH_MS = 60000;
const miles = (n) => (n == null ? "—" : `${Math.round(Number(n)).toLocaleString("en-US")} mi`);
function fmtWhen(iso) { return iso ? toUtcDate(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"; }
function ago(iso) { if (!iso) return "—"; const s = (Date.now() - toUtcDate(iso).getTime()) / 1000; if (s < 90) return "just now"; const m = Math.round(s / 60); return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`; }

const STATUS_STYLE = {
  "En Route": { bg: "rgba(76,174,109,0.14)", fg: "#2f9e5f" },
  "At Pickup": { bg: "rgba(37,99,235,0.12)", fg: "#2563eb" },
  "Stopped": { bg: "rgba(100,116,139,0.14)", fg: "#64748b" },
  "Not Progressing": { bg: "rgba(242,163,61,0.16)", fg: "#c07f1f" },
  "GPS Stale": { bg: "rgba(220,38,38,0.10)", fg: "#dc2626" },
};
const RISK_STYLE = { "On time": "#2f9e5f", "At risk": "#c07f1f", "Late": "#dc2626" };
const FLAG_LABEL = { next_load_canceled: "Load canceled", next_load_missing: "Load missing", reassigned: "Reassigned", no_appointment: "No appt", };

export default function EnRoutePickup() {
  const [scope, setScope] = useState(isAdmin() ? "all" : "mine");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [route, setRoute] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      axios.get(`${apiBaseUrl}/fleet/en-route-to-pickup`, { ...authHeaders(), params: { scope } })
        .then((res) => { if (alive) { setData(res.data); setError(null); } })
        .catch((err) => { if (alive) setError(err.response?.data?.detail || err.message); })
        .finally(() => { if (alive) setLoaded(true); });
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, [scope]);

  const trucks = data?.trucks || [];

  return (
    <div>
      {route && <EnRouteMapModal {...route} onClose={() => setRoute(null)} />}

      <div style={S.header}>
        <div>
          <h1 style={S.title}>En Route to Pickup <span style={S.live}><Radio size={11} /> live</span></h1>
          <div style={S.sub}>Trucks with no current load, deadheading toward a booked pickup. Refreshes every 60s · remaining distance is straight‑line.</div>
        </div>
        <div style={S.scopeToggle}>
          <button type="button" onClick={() => setScope("mine")} style={{ ...S.scopeBtn, ...(scope === "mine" ? S.scopeActive : {}) }}>My trucks</button>
          <button type="button" onClick={() => setScope("all")} style={{ ...S.scopeBtn, ...(scope === "all" ? S.scopeActive : {}) }}>All trucks</button>
        </div>
      </div>

      {error && <div style={S.err}>Couldn't load: {error}</div>}
      {!loaded && <LoadingState label="Loading en‑route board…" />}
      {loaded && trucks.length === 0 && !error && (
        <div style={S.card}><div style={S.empty}>No trucks are currently deadheading to a pickup.</div></div>
      )}

      {trucks.length > 0 && (
        <div style={S.card}>
          <div style={S.metaLine}>{trucks.length} truck{trucks.length === 1 ? "" : "s"} en route · updated {data?.as_of ? toUtcDate(data.as_of).toLocaleTimeString() : "—"}</div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Truck</th><th style={S.th}>Driver</th><th style={S.th}>Next Load</th>
                <th style={S.th}>Pickup</th><th style={S.th}>Appointment</th>
                <th style={S.thR}>Deadhead Driven</th><th style={S.thR}>Miles Remaining</th>
                <th style={S.th}>ETA</th><th style={S.th}>Status</th><th style={S.th}>Risk</th>
                <th style={S.th}>Last GPS</th><th style={S.th} />
              </tr></thead>
              <tbody>
                {trucks.map((t) => {
                  const ss = STATUS_STYLE[t.status] || STATUS_STYLE["Stopped"];
                  return (
                    <tr key={t.truck_id} style={S.tr}>
                      <td style={{ ...S.td, fontWeight: 700 }}>#{t.unit_number || "?"}</td>
                      <td style={{ ...S.td, color: t.driver_name ? "var(--ink-2)" : "#c07f1f" }}>{t.driver_name || "—"}</td>
                      <td style={S.td}>{t.next_load_id ? String(t.next_load_id).slice(0, 8) : "—"}</td>
                      <td style={S.td}>{t.pickup_location || "—"}</td>
                      <td style={S.td}>{fmtWhen(t.pickup_appointment)}</td>
                      <td style={S.tdR}>{miles(t.deadhead_driven_miles)}</td>
                      <td style={S.tdR}>{miles(t.miles_remaining)}</td>
                      <td style={S.td}>{fmtWhen(t.eta)}</td>
                      <td style={S.td}>
                        <span style={{ ...S.chip, background: ss.bg, color: ss.fg }}>
                          {t.status === "Not Progressing" && <Navigation size={11} />}{t.status}
                        </span>
                        {(t.flags || []).map((f) => <span key={f} style={S.flag}>{FLAG_LABEL[f] || f}</span>)}
                      </td>
                      <td style={S.td}>{t.pickup_risk ? <span style={{ color: RISK_STYLE[t.pickup_risk], fontWeight: 700 }}>{t.pickup_risk}</span> : "—"}</td>
                      <td style={S.td}>{ago(t.last_gps_at)}</td>
                      <td style={S.td}>
                        <button style={S.viewBtn} onClick={() => setRoute({ truckId: t.truck_id, nextLoadId: t.next_load_id, unitNumber: t.unit_number, driverName: t.driver_name, pickupLabel: t.pickup_location })}>
                          <MapPin size={12} /> View Route
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={S.caption}>
            Deadhead Driven = actual GPS breadcrumb miles already driven toward this pickup. Miles Remaining = straight‑line distance from the truck's current position to the pickup (not a routed distance); ETA is a straight‑line estimate. Status: En Route (closing distance) · Not Progressing (moving but not closing) · Stopped (fresh GPS, little movement) · At Pickup (inside geofence) · GPS Stale (telematics &gt; 20 min old).
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0, display: "flex", alignItems: "center", gap: 10 },
  live: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", color: "#fff", background: "#2f9e5f", borderRadius: 999, padding: "2px 8px" },
  sub: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 720 },
  scopeToggle: { display: "inline-flex", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" },
  scopeBtn: { fontSize: 12, fontWeight: 600, color: "var(--ink-2)", background: "var(--card)", border: "none", padding: "7px 12px", cursor: "pointer" },
  scopeActive: { background: "var(--brand, #2563eb)", color: "#fff" },
  err: { background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626", borderRadius: 8, padding: "12px 16px", fontSize: 13, marginBottom: 16 },
  card: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: 12, boxShadow: "var(--shadow-xs)" },
  empty: { fontSize: 12.5, color: "var(--muted)", padding: 12 },
  metaLine: { fontSize: 12, color: "var(--muted)", padding: "2px 4px 10px" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5, whiteSpace: "nowrap" },
  th: { textAlign: "left", padding: "8px 8px", color: "var(--muted)", fontWeight: 600, borderBottom: "1px solid var(--line)" },
  thR: { textAlign: "right", padding: "8px 8px", color: "var(--muted)", fontWeight: 600, borderBottom: "1px solid var(--line)" },
  tr: { borderBottom: "1px solid var(--line)" },
  td: { padding: "8px 8px", color: "var(--ink-2)", verticalAlign: "middle" },
  tdR: { padding: "8px 8px", color: "var(--ink-2)", textAlign: "right" },
  chip: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: "3px 9px" },
  flag: { marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: "#c07f1f", background: "rgba(242,163,61,0.12)", borderRadius: 999, padding: "2px 7px" },
  viewBtn: { display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: "var(--brand, #2563eb)", background: "var(--card)", border: "1px solid var(--brand, #2563eb)", borderRadius: 7, padding: "5px 10px", cursor: "pointer", whiteSpace: "nowrap" },
  caption: { fontSize: 11, color: "var(--muted)", marginTop: 10, lineHeight: 1.5, maxWidth: 1000, whiteSpace: "normal" },
};
